import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { describeError } from '../../lib/api.ts';
import { api, type CandidateHistoryEntry } from '../../lib/client.ts';
import { ChangeValues } from '../records/change-values.tsx';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';

const ACTION_LABEL: Record<string, string> = {
  create: 'created',
  update: 'updated',
  delete: 'deleted',
  restore: 'restored',
};

/**
 * The History panel (RELAY-942): a candidate's audited changes, newest first, one line per
 * field — "stage: Screening → Interview" — with who and when. It reads the audit log through
 * `/candidates/:id/history`, so it shows exactly what an auditor would, no separate feed.
 */
export function HistoryPanel({
  candidateId,
  title,
  onClose,
}: {
  candidateId: string;
  title: string;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>History · {title}</DialogTitle>
          <DialogDescription>Every change, newest first.</DialogDescription>
        </DialogHeader>
        <HistoryList candidateId={candidateId} />
      </DialogContent>
    </Dialog>
  );
}

/** The timeline itself: the panel wraps it in a dialog, the detail page shows it in a tab. */
export function HistoryList({ candidateId }: { candidateId: string }) {
  const q = useInfiniteQuery({
    queryKey: ['candidate-history', candidateId],
    queryFn: ({ pageParam }) => api.candidates.history(candidateId, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <>
      {q.isPending && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {q.error != null && (
        <p className="text-danger text-sm" role="alert">
          {describeError(q.error)}
        </p>
      )}
      {!q.isPending && items.length === 0 && q.error == null && (
        <p className="text-muted-foreground text-sm">No changes recorded.</p>
      )}
      <ol className="flex flex-col gap-3" data-testid="history-list">
        {items.map((e) => (
          <HistoryItem key={e.id} entry={e} />
        ))}
      </ol>
      {q.hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => void q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </>
  );
}

function HistoryItem({ entry }: { entry: CandidateHistoryEntry }) {
  const changes = Object.entries(entry.changes ?? {});
  return (
    <li className="border-border rounded-md border p-3 text-sm" data-testid="history-item">
      <div className="text-muted-foreground flex flex-wrap justify-between gap-2 text-xs">
        <span>
          <span className="text-foreground font-medium">{entry.actor.label ?? 'Someone'}</span>{' '}
          {ACTION_LABEL[entry.action] ?? entry.action}
        </span>
        <time dateTime={entry.occurredAt}>{new Date(entry.occurredAt).toLocaleString()}</time>
      </div>
      {changes.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {changes.map(([field, c]) => (
            <li key={field} className="flex flex-wrap gap-x-2">
              <span className="font-medium">{field.replace(/^custom\./, '')}</span>
              <ChangeValues change={c} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
